import { v4 as uuidv4 } from 'uuid'
import type { Piece, PieceLocation, PieceLocationType, PieceTimeIndication } from '@dhl/pieces'
import {
  type Location,
  LockersDeBuren,
  LockersDHL,
  LockersSmartmile,
  ParcelStation,
  type Receiver,
  type ReceiverParcelSummary,
  useGetParcelsHook,
} from '@dhl/receiver-parcel-api'

const mapLocationType = (apiLocationType: Location['locationType'] | undefined): PieceLocationType | undefined => {
  switch (apiLocationType) {
    case undefined:
      return undefined

    case 'ADDRESS':
      return 'ADDRESS'

    case 'NEIGHBOUR':
      return 'NEIGHBOUR'

    case 'AGREED_PLACE':
      return 'AGREEDPLACE'

    case 'PARCEL_SHOP':
    case 'SERVICEPOINT':
      return 'SERVICEPOINT'

    case 'TERMINAL':
      return 'TERMINAL'

    case 'RETURN':
      return 'RETURN'

    case 'PARCEL_STATION':
      return 'PARCELSTATION'

    default:
      return 'UNKNOWN'
  }
}

const mapLocation = (location: Location | Receiver | undefined): PieceLocation | undefined => {
  if (!location) {
    return undefined
  }
  return {
    name: location.name?.trim() === '' ? undefined : location.name,
    locationType: mapLocationType((location as Location).locationType),
    address: {
      countryCode: location.address?.countryCode,
      postalCode: location.address?.postalCode,
      city: location.address?.city,
      street: location.address?.street,
      houseNumber: location.address?.houseNumber,
      houseNumberSuffix: location.address?.houseNumberSuffix,
      suburb: undefined,
      additionalAddressLine: undefined,
      lines: [],
    },
  }
}

const parcelSummaryToPiece = (apiPiece: ReceiverParcelSummary): Piece => ({
  orderId: apiPiece.orderId,
  pieceId: apiPiece.parcelId,
  type: 'outgoing',
  barcode: apiPiece.barcode ?? '',
  destination: mapLocation(apiPiece.destination),
  receiver: mapLocation(apiPiece.receiver),
  sender: {
    name: apiPiece.sender?.name,
  },
  timeCreated: apiPiece.createdAt,
  receivingTimeIndication: apiPiece.receivingTimeIndication as unknown as PieceTimeIndication,
  status: apiPiece.status ?? '',
  category: apiPiece.category,
  intervenable: apiPiece.intervenable,
  intervenableBarcode: null,
  totalPrice: null,
  receivedDaysAgo: null,
  returnedDaysAgo: null,
  isLockerDelivery: [LockersDHL, LockersDeBuren, LockersSmartmile, ParcelStation].includes(
    `${apiPiece.destination?.servicePointFormat}`
  ),
  isReturn: false,
  isRegistered: apiPiece.isRegistered,
  identifier: apiPiece.barcode ?? uuidv4(),
  isClimateNeutralDelivery: false,
  undeliverableSinceDaysAgo: null,
  isUndeliverable: false,
  returnable: null,
  hasDeliveryCode: null,
  hasLockerCode: null,
  hasServicePointPickupCode: null,
  source: 'C2C',
})

export const useGetParcelOutgoingPieces = () => {
  const getParcels = useGetParcelsHook()

  return async (): Promise<Piece[]> => {
    const response = await getParcels({ tab: 'outgoing' })
    const parcels = response.parcels ?? []
    return parcels.map(parcelSummaryToPiece)
  }
}
